import { useMemo, type CSSProperties } from 'react';
import Select, {
  type OptionProps,
  type SingleValue,
  type StylesConfig
} from 'react-select';

export type UrlScope = 'url' | 'path' | 'host' | 'root';

export type StartCrawlScopeSettings = {
  domain_scope: 'host' | 'root';
  include_urls_is_enable: boolean;
  include_urls_value: string;
};

export const URL_SCOPES: UrlScope[] = ['url', 'path', 'host', 'root'];

export const isUrlScope = (value: unknown): value is UrlScope =>
  typeof value === 'string' && URL_SCOPES.includes(value as UrlScope);

export const buildStartCrawlSettings = (
  scope: UrlScope,
  startUrl: string
): StartCrawlScopeSettings => {
  const url = startUrl.trim();
  if (scope === 'root') {
    return {
      domain_scope: 'root',
      include_urls_is_enable: false,
      include_urls_value: ''
    };
  }
  if (scope === 'url') {
    return {
      domain_scope: 'host',
      include_urls_is_enable: true,
      include_urls_value: url
    };
  }
  if (scope === 'path') {
    return {
      domain_scope: 'host',
      include_urls_is_enable: true,
      include_urls_value: url.endsWith('*') ? url : `${url}*`
    };
  }
  return {
    domain_scope: 'host',
    include_urls_is_enable: false,
    include_urls_value: ''
  };
};

export const includePatternsFromSettings = (
  settings: StartCrawlScopeSettings
): string[] => (settings.include_urls_value ? [settings.include_urls_value] : []);

export const inferUrlScope = (
  settings: Record<string, unknown> | null | undefined
): UrlScope => {
  if (!settings) return 'host';
  if (isUrlScope(settings.url_scope)) return settings.url_scope;
  const includeOn =
    settings.include_urls_is_enable === true ||
    settings.include_urls_is_enable === 'true' ||
    settings.include_urls_is_enable === 'True';
  const raw = settings.include_urls_value;
  const value = Array.isArray(raw)
    ? String(raw[0] ?? '')
    : String(raw ?? '');
  if (includeOn && value.trim()) {
    return value.trim().endsWith('*') ? 'path' : 'url';
  }
  if (settings.domain_scope === 'root') return 'root';
  return 'host';
};

type UrlScopeOption = {
  value: UrlScope;
  label: string;
  description: string;
  example: string;
};

const buildOptions = (domain: string): UrlScopeOption[] => {
  const host = domain || 'example.com';
  return [
    {
      value: 'url',
      label: 'نشانی دقیق',
      description: 'فقط همین نشانی',
      example: `${host}/path/`
    },
    {
      value: 'path',
      label: 'مسیر',
      description: 'مسیر به‌همراه زیرپوشه‌ها',
      example: `${host}/path/*`
    },
    {
      value: 'host',
      label: 'دامنه',
      description: 'فقط همین دامنه',
      example: `${host}/*`
    },
    {
      value: 'root',
      label: 'زیردامنه‌ها',
      description: 'دامنه به‌همراه زیردامنه‌ها',
      example: `*.${host}/*`
    }
  ];
};

interface UrlScopeDropdownProps {
  domain: string;
  value: UrlScope;
  onChange: (value: UrlScope) => void;
  variant?: 'input-group' | 'form';
  id?: string;
  /** Narrower control and menu (e.g. crawl settings drawer) */
  compact?: boolean;
}

const menuWidth = '20rem';
const compactMenuWidth = '14rem';
const inputGroupControlMinWidth = '10.5rem';
const inputGroupMenuWidth = '13.5rem';

const UrlScopeDropdown = ({
  domain,
  value,
  onChange,
  variant = 'input-group',
  id = 'crawl-url-scope',
  compact = false
}: UrlScopeDropdownProps): React.JSX.Element => {
  const options = useMemo(() => buildOptions(domain), [domain]);
  const selected =
    options.find(option => option.value === value) ?? options[2];
  const isForm = variant === 'form';

  const handleChange = (option: SingleValue<UrlScopeOption>) => {
    if (option) onChange(option.value);
  };

  const styles = useMemo(
    () => createScopeSelectStyles(isForm, compact),
    [compact, isForm]
  );

  return (
    <Select<UrlScopeOption, false>
      inputId={id}
      instanceId={id}
      classNamePrefix="react-select"
      className={
        isForm
          ? compact
            ? 'rounded-2 w-auto crawl-url-scope--compact'
            : 'rounded-2 w-100'
          : undefined
      }
      options={options}
      value={selected}
      onChange={handleChange}
      isSearchable={false}
      closeMenuOnSelect={true}
      menuShouldScrollIntoView={true}
      menuPlacement="auto"
      maxMenuHeight={320}
      styles={styles}
      components={{ Option: UrlScopeOptionLayout }}
      menuPortalTarget={
        typeof document !== 'undefined' ? document.body : undefined
      }
      noOptionsMessage={() => 'موردی یافت نشد'}
    />
  );
};

export default UrlScopeDropdown;

const createScopeSelectStyles = (
  isForm: boolean,
  compact: boolean
): StylesConfig<UrlScopeOption, false> => {
  const formMenuWidth = compact ? compactMenuWidth : menuWidth;
  return {
  menuPortal: baseStyles => ({ ...baseStyles, zIndex: 1060 }),
  menu: baseStyles => ({
    ...baseStyles,
    zIndex: 9999,
    width: isForm ? formMenuWidth : inputGroupMenuWidth,
    minWidth: isForm ? formMenuWidth : inputGroupMenuWidth
  }),
  control: baseStyles => ({
    ...baseStyles,
    minHeight: 38,
    minWidth: isForm
      ? compact
        ? compactMenuWidth
        : undefined
      : inputGroupControlMinWidth,
    maxWidth: isForm && compact ? compactMenuWidth : undefined,
    width: isForm ? (compact ? compactMenuWidth : '100%') : undefined,
    paddingLeft: isForm ? '10px' : '0',
    paddingRight: isForm ? '0' : '10px',
    cursor: 'pointer',
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important',
    borderRadius: isForm ? undefined : '0 5px 5px 0',
    borderColor: 'var(--falcon-gray-300)'
  }),
  valueContainer: baseStyles => ({
    ...baseStyles,
    paddingLeft: isForm ? 0 : 12,
    paddingRight: isForm ? 0 : 8
  }),
  singleValue: baseStyles => ({
    ...baseStyles,
    color: 'var(--falcon-body-color)'
  }),
  input: baseStyles => ({
    ...baseStyles,
    color: 'var(--falcon-emphasise) !important',
    boxShadow: 'none !important'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: baseStyles => ({
    ...baseStyles,
    paddingLeft: 4,
    paddingRight: 4
  })
};
};

const UrlScopeOptionLayout = (props: OptionProps<UrlScopeOption, false>) => {
  const { innerProps, innerRef, isSelected, data } = props;
  return (
    <>
      <style>{`
        .url-scope-select-option:hover {
          background-color: var(--falcon-gray-link-hover-300) !important;
        }
        .url-scope-select-option {
          background-color: var(--falcon-tertiary-bg) !important;
        }
      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1 url-scope-select-option"
      >
        <div className="fw-semi-bold text-1000 fs--1">{data.label}</div>
        <div className="text-secondary fs--2">{data.description}</div>
        <div
          className="fs--2 text-500 font-monospace"
          dir="ltr"
          style={{ direction: 'ltr', textAlign: 'left' }}
        >
          {data.example}
        </div>
      </div>
    </>
  );
};
